import React from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../../constants/theme';

/**
 * TimelineRow — a single entry in the couple's relationship timeline.
 *
 * The `gold` flag marks the final "wedding" entry with a special
 * highlighted appearance to distinguish it from earlier chapters.
 *
 * @param {object}  props
 * @param {string}  props.gem    - Emoji icon for the milestone.
 * @param {string}  props.level  - Chapter / level label.
 * @param {string}  props.title  - Short milestone title.
 * @param {string}  props.desc   - Longer description sentence.
 * @param {boolean} props.gold   - Whether this is the golden finale entry.
 */
function TimelineRow({ gem, level, title, desc, gold }) {
  const borderColour = gold ? COLOURS.primary : COLOURS.primaryLight;
  const outerShadow  = gold
    ? `0 0 0 2px ${COLOURS.cream}, 0 0 0 3px ${COLOURS.primaryLight}`
    : `0 0 0 2px ${COLOURS.cream}, 0 0 0 3px ${COLOURS.borderGold}`;

  return (
    <div
      style={{
        display:    'flex',
        alignItems: 'flex-start',
        gap:        10,
        marginBottom: 16,
        fontFamily: FONT,
      }}
    >
      {/* Gem / milestone icon */}
      <div
        aria-hidden="true"
        style={{
          width:        44,
          height:       44,
          flexShrink:   0,
          background:   COLOURS.cream,
          border:       `3px solid ${borderColour}`,
          display:      'flex',
          alignItems:   'center',
          justifyContent: 'center',
          fontSize:     18,
          boxShadow:    outerShadow,
        }}
      >
        {gem}
      </div>

      {/* Text content */}
      <div style={{ paddingTop: 6 }}>
        <div style={{ fontSize: 6, color: COLOURS.primaryLight, marginBottom: 3 }}>
          {level}
        </div>
        <div
          style={{
            fontSize: 8,
            color:    gold ? COLOURS.primary : COLOURS.textDark,
            marginBottom: 3,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 6, color: COLOURS.textMid, lineHeight: 1.8 }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

TimelineRow.propTypes = {
  gem:   PropTypes.string.isRequired,
  level: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  desc:  PropTypes.string.isRequired,
  gold:  PropTypes.bool,
};

TimelineRow.defaultProps = {
  gold: false,
};

export default TimelineRow;
